import React, { useState, useRef } from 'react';
import { uploadService } from '../api/services';
import './BlockEditor.css';

const LANGUAGES = [
  'javascript', 'typescript', 'python', 'java', 'c', 'cpp', 'csharp', 'go', 'rust',
  'ruby', 'php', 'swift', 'kotlin', 'sql', 'html', 'css', 'scss', 'bash', 'shell',
  'json', 'yaml', 'xml', 'markdown', 'dockerfile', 'nginx', 'plaintext',
];

// 內嵌圖片單檔上限（MB）；超過時提示使用者改用附件上傳
const MAX_IMG_MB = 10;

const blockLabel = (type) => (type === 'text' ? '📝 文字' : type === 'code' ? '💻 程式碼' : '🖼 圖片');

const BlockEditor = ({ blocks, onChange }) => {
  const [dragIndex, setDragIndex] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [imgError, setImgError] = useState('');
  const fileInputRef = useRef(null);
  // 記住「插入圖片」按鈕被點擊時的目標位置，待選檔完成後在該位置之後插入圖片區塊
  const pendingIndexRef = useRef(blocks.length - 1);

  const updateBlock = (index, updates) => {
    const newBlocks = [...blocks];
    newBlocks[index] = { ...newBlocks[index], ...updates };
    onChange(newBlocks);
  };

  const addBlock = (type, afterIndex = blocks.length - 1) => {
    const newBlock = type === 'code'
      ? { type: 'code', language: 'javascript', content: '' }
      : { type: 'text', content: '' };
    const newBlocks = [...blocks];
    newBlocks.splice(afterIndex + 1, 0, newBlock);
    onChange(newBlocks);
  };

  const removeBlock = (index) => {
    if (blocks.length <= 1) return;
    const newBlocks = blocks.filter((_, i) => i !== index);
    onChange(newBlocks);
  };

  const moveBlock = (from, to) => {
    if (to < 0 || to >= blocks.length) return;
    const newBlocks = [...blocks];
    const [moved] = newBlocks.splice(from, 1);
    newBlocks.splice(to, 0, moved);
    onChange(newBlocks);
  };

  // 上傳圖片並在 afterIndex 之後插入圖片區塊（僅儲存 file_path，顯示時再以 getFileUrl 還原，確保跨環境可攜）
  const insertImage = async (file, afterIndex) => {
    if (!file) return;
    if (!file.type || !file.type.startsWith('image/')) { setImgError('只能插入圖片檔'); return; }
    if (file.size > MAX_IMG_MB * 1024 * 1024) { setImgError(`圖片超過 ${MAX_IMG_MB}MB 上限，請改用下方附件上傳`); return; }
    setImgError('');
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('files', file);
      const res = await uploadService.upload(fd);
      const att = res.data?.[0];
      if (att) {
        const newBlocks = [...blocks];
        const at = (afterIndex == null || afterIndex < 0) ? blocks.length - 1 : afterIndex;
        newBlocks.splice(at + 1, 0, { type: 'image', file_path: att.file_path, alt: '' });
        onChange(newBlocks);
      }
    } catch (err) {
      setImgError(err.response?.data?.error || err.message || '圖片上傳失敗');
    } finally {
      setUploading(false);
    }
  };

  // 貼上事件：偵測剪貼簿中的圖片並直接插入；非圖片則維持預設貼上行為
  const handlePaste = (e, index) => {
    const items = e.clipboardData?.items;
    if (!items) return;
    for (const it of items) {
      if (it.kind === 'file' && it.type && it.type.startsWith('image/')) {
        const file = it.getAsFile();
        if (file) { e.preventDefault(); insertImage(file, index); return; }
      }
    }
  };

  const triggerImagePicker = (afterIndex) => {
    pendingIndexRef.current = afterIndex;
    fileInputRef.current?.click();
  };

  const onFilePicked = (e) => {
    const file = e.target.files?.[0];
    if (file) insertImage(file, pendingIndexRef.current);
    e.target.value = '';
  };

  return (
    <div className="block-editor">
      {/* 共用隱藏檔案輸入：供「插入圖片」按鈕觸發 */}
      <input ref={fileInputRef} type="file" accept="image/*" style={{ display: 'none' }} onChange={onFilePicked} />
      {uploading && <div className="block-uploading">圖片上傳中...</div>}
      {imgError && <div className="block-img-error">⚠️ {imgError}</div>}

      {blocks.map((block, index) => (
        <div
          key={index}
          className={`block-item block-${block.type} ${dragIndex === index ? 'dragging' : ''}`}
          draggable
          onDragStart={() => setDragIndex(index)}
          onDragOver={(e) => e.preventDefault()}
          onDrop={() => { if (dragIndex !== null && dragIndex !== index) { moveBlock(dragIndex, index); setDragIndex(null); }}}
          onDragEnd={() => setDragIndex(null)}
        >
          <div className="block-toolbar">
            <div className="block-type-indicator">
              <span className="block-drag-handle" title="拖拉排序">⠿</span>
              {blockLabel(block.type)}
            </div>
            {block.type === 'code' && (
              <select
                className="block-lang-select"
                value={block.language || 'javascript'}
                onChange={(e) => updateBlock(index, { language: e.target.value })}
              >
                {LANGUAGES.map(lang => (
                  <option key={lang} value={lang}>{lang}</option>
                ))}
              </select>
            )}
            <div className="block-actions">
              <button type="button" className="block-btn" onClick={() => moveBlock(index, index - 1)} disabled={index === 0} title="上移">↑</button>
              <button type="button" className="block-btn" onClick={() => moveBlock(index, index + 1)} disabled={index === blocks.length - 1} title="下移">↓</button>
              <button type="button" className="block-btn block-btn-danger" onClick={() => removeBlock(index)} disabled={blocks.length <= 1} title="刪除">✕</button>
            </div>
          </div>

          {block.type === 'text' ? (
            <textarea
              className="block-textarea"
              value={block.content}
              onChange={(e) => updateBlock(index, { content: e.target.value })}
              onPaste={(e) => handlePaste(e, index)}
              placeholder="輸入文字內容...（可直接貼上圖片）"
              rows={4}
            />
          ) : block.type === 'code' ? (
            <textarea
              className="block-code-textarea"
              value={block.content}
              onChange={(e) => updateBlock(index, { content: e.target.value })}
              placeholder="輸入程式碼..."
              rows={8}
              spellCheck={false}
            />
          ) : (
            <div className="block-image-wrap">
              <img className="block-image-preview" src={uploadService.getFileUrl(block.file_path)} alt={block.alt || ''} />
              <input
                className="block-image-alt"
                value={block.alt || ''}
                onChange={(e) => updateBlock(index, { alt: e.target.value })}
                placeholder="圖片說明（選填）"
              />
            </div>
          )}

          {/* Add block buttons between blocks */}
          <div className="block-add-row">
            <button type="button" className="block-add-btn" onClick={() => addBlock('text', index)} title="新增文字區塊">+ 文字</button>
            <button type="button" className="block-add-btn" onClick={() => addBlock('code', index)} title="新增程式碼區塊">+ 程式碼</button>
            <button type="button" className="block-add-btn" onClick={() => triggerImagePicker(index)} title="插入圖片">+ 圖片</button>
          </div>
        </div>
      ))}

      {blocks.length === 0 && (
        <div className="block-empty">
          <button type="button" className="block-add-btn" onClick={() => addBlock('text', -1)}>+ 文字</button>
          <button type="button" className="block-add-btn" onClick={() => addBlock('code', -1)}>+ 程式碼</button>
          <button type="button" className="block-add-btn" onClick={() => triggerImagePicker(-1)}>+ 圖片</button>
        </div>
      )}
    </div>
  );
};

export default BlockEditor;
